/**
 * Maps resolved Crew next-task kinds to stop-advance event types.
 * The CTA must submit the event implied by resolveCrewNextTask — never
 * re-derive from stop.status (that breaks RTB recovery → arrive_stop).
 */
import type { CrewCargoCheckState, CrewNextTaskKind } from './crewNextTask.ts';

export type StopAdvanceEventType =
  | 'arrive_stop'
  | 'start_service'
  | 'end_service'
  | 'depart_stop';

const STOP_ADVANCE_KINDS = new Set<CrewNextTaskKind>([
  'arrive_stop',
  'start_service',
  'end_service',
  'depart_stop',
]);

/** Strict allow-list: only stop-advance kinds map to an event; others return null. */
export function eventTypeFromNextTask(kind: CrewNextTaskKind): StopAdvanceEventType | null {
  if (!STOP_ADVANCE_KINDS.has(kind)) return null;
  return kind as StopAdvanceEventType;
}

/** Minimal shape of an offline-queued crew action (see offline/queue.ts QueuedEvent). */
export type QueuedCrewAction = {
  type: string;
  payload: Record<string, unknown>;
  status?: string;
};

type OverlayStop = {
  id: string;
  type: string;
  status: string;
  podId?: string | null;
  hasException?: boolean | null;
  cargoCheck?: OverlayCargoCheck | null;
};

/** Subset of the server's per-stop cargo check DTO that the overlay updates. */
export type OverlayCargoCheck = CrewCargoCheckState;

const STOP_STATUS_ORDER = ['pending', 'arrived', 'servicing', 'serviced', 'departed'];

const STATUS_AFTER_EVENT: Record<StopAdvanceEventType, string> = {
  arrive_stop: 'arrived',
  start_service: 'servicing',
  end_service: 'serviced',
  depart_stop: 'departed',
};

/** Placeholder podId for a stop whose signature is saved on the phone but not uploaded yet. */
export const QUEUED_POD_ID = 'queued-offline';

function rank(status: string): number {
  const i = STOP_STATUS_ORDER.indexOf(status);
  return i === -1 ? -1 : i;
}

/**
 * Show the trip as it will look once the phone's saved (offline) actions reach the
 * server, so the guided next-task moves on instead of offering the same action again.
 * Pure + forward-only: a stop never moves backwards, and failed items are ignored
 * (the server is still authoritative once the queue syncs).
 */
export function applyQueuedCrewActions<
  S extends OverlayStop,
  T extends { id: string; status: string; stops?: S[] },
>(trip: T, actions: QueuedCrewAction[]): T {
  let next = trip;
  const patchStop = (stopId: string, patch: (s: S) => S) => {
    if (!next.stops?.some((s) => s.id === stopId)) return;
    next = { ...next, stops: next.stops.map((s) => (s.id === stopId ? patch(s) : s)) };
  };

  for (const action of actions) {
    if (action.status === 'failed') continue;
    const p = action.payload ?? {};
    if (action.type === 'stop_advance' && p.tripId === trip.id && typeof p.stopId === 'string') {
      const eventType = p.eventType as StopAdvanceEventType;
      let target = STATUS_AFTER_EVENT[eventType];
      if (!target) continue;
      patchStop(p.stopId, (s) => {
        // Depot arrival completes the return-to-base stop in one step on the server.
        if (eventType === 'arrive_stop' && s.type === 'return_to_base') target = 'departed';
        return rank(target) > rank(s.status) ? { ...s, status: target } : s;
      });
    } else if (action.type === 'crew_event' && p.tripId === trip.id) {
      if (p.eventType === 'start_trip' && (next.status === 'dispatched' || next.status === 'assigned')) {
        next = { ...next, status: 'executing' };
      } else if (p.eventType === 'end_trip' && next.status !== 'completed' && next.status !== 'cancelled') {
        next = { ...next, status: 'completed' };
      }
    } else if (action.type === 'pod' && typeof p.tripStopId === 'string') {
      patchStop(p.tripStopId, (s) => (s.podId ? s : { ...s, podId: QUEUED_POD_ID }));
    } else if (action.type === 'cargo_check' && p.tripId === trip.id && typeof p.stopId === 'string') {
      // A saved cargo check completes the stop's cargo step (server re-checks on sync).
      patchStop(p.stopId, (s) => {
        if (!s.cargoCheck) return s;
        const counted = typeof p.countedPieces === 'number' ? p.countedPieces : null;
        const expected = s.cargoCheck.expectedPieces ?? null;
        return {
          ...s,
          cargoCheck: {
            ...s.cargoCheck,
            completed: true,
            countedPieces: counted,
            picName: typeof p.picName === 'string' ? p.picName : null,
            mismatch: p.mode === 'piece_count' && expected != null && counted !== expected,
            mismatchReason: typeof p.mismatchReason === 'string' ? p.mismatchReason : null,
            confirmedAt: typeof p.deviceTimestamp === 'string' ? p.deviceTimestamp : s.cargoCheck.confirmedAt ?? null,
          },
        };
      });
    } else if (action.type === 'exception' && p.tripId === trip.id) {
      // A filed exception unblocks the stop's scan gate on the server (stop.hasException).
      if (typeof p.stopId === 'string') {
        patchStop(p.stopId, (s) => (s.hasException ? s : { ...s, hasException: true }));
      } else if (next.stops) {
        next = {
          ...next,
          stops: next.stops.map((s) => (s.status === 'departed' ? s : { ...s, hasException: true })),
        };
      }
    }
  }
  return next;
}
